import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import type { IotKanal, IotZarizeni, TypKanalu } from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import { formatNaposledy, type StavSpojeni } from './iotStav';

type ParametrRucni = { label: string; min: number; max: number; vychozi: number | null; pole: 'sekund' | 'hodnota'; povinny: boolean };

/** Ruční akce s parametrem: popisek pole, meze, výchozí hodnota, kam se hodnota posílá; undefined = bez parametru. */
export const parametrRucni = (akce: string, typ: TypKanalu): ParametrRucni | undefined => {
    if (akce === 'PULS') return { label: 'Délka pulsu (s)', min: 1, max: 3600, vychozi: 2, pole: 'sekund', povinny: true };
    if (akce === 'ZAPNOUT_NA_MINUT') return { label: 'Doba (minuty)', min: 1, max: 720, vychozi: 30, pole: 'hodnota', povinny: true };
    if (akce === 'COVER_POZICE') return { label: 'Poloha (%)', min: 0, max: 100, vychozi: 50, pole: 'hodnota', povinny: true };
    if (akce === 'ZAPNOUT' && typ === 'light') return { label: 'Jas (%)', min: 1, max: 100, vychozi: null, pole: 'hodnota', povinny: false };
    return undefined;
};

export const parametrPlatny = (par: ParametrRucni, h: number | null): boolean =>
    h == null ? !par.povinny : Number.isInteger(h) && h >= par.min && h <= par.max;

/** Nadpis, text hlavního tlačítka a věta pod polem podle akce (spec UX IoT 3.1). */
export const textyRucniAkce = (akce: string, typ: TypKanalu, vystup: string, n: number | null): { nadpis: string; tlacitko: string; veta?: string; napoveda?: string } => {
    const svetlo = typ === 'light';
    const cislo = n == null ? '…' : String(n);
    switch (akce) {
        case 'ZAPNOUT':
            return svetlo
                ? { nadpis: `Rozsvítit ${vystup}?`, tlacitko: n == null ? 'Rozsvítit' : `Rozsvítit na ${n} %`, napoveda: 'Prázdné = jas podle zařízení.' }
                : { nadpis: `Zapnout ${vystup}?`, tlacitko: 'Zapnout' };
        case 'VYPNOUT':
            return svetlo ? { nadpis: `Zhasnout ${vystup}?`, tlacitko: 'Zhasnout' } : { nadpis: `Vypnout ${vystup}?`, tlacitko: 'Vypnout' };
        case 'ZAPNOUT_NA_MINUT':
            return svetlo
                ? { nadpis: `Rozsvítit ${vystup} na dobu?`, tlacitko: `Rozsvítit na ${cislo} min`, veta: 'Po uplynutí se výstup sám vypne.' }
                : { nadpis: `Zapnout ${vystup} na dobu?`, tlacitko: `Zapnout na ${cislo} min`, veta: 'Po uplynutí se výstup sám vypne.' };
        case 'PULS':
            return { nadpis: `Sepnout ${vystup} pulsem?`, tlacitko: `Sepnout na ${cislo} s`, veta: `Výstup se sepne a po ${cislo} s sám rozepne.` };
        case 'COVER_OTEVRIT':
            return { nadpis: `Otevřít ${vystup}?`, tlacitko: 'Otevřít' };
        case 'COVER_ZAVRIT':
            return { nadpis: `Zavřít ${vystup}?`, tlacitko: 'Zavřít' };
        case 'COVER_POZICE':
            return { nadpis: `Nastavit polohu ${vystup}?`, tlacitko: `Nastavit na ${cislo} %`, veta: 'Funguje jen u kalibrované rolety.' };
        default:
            return { nadpis: `${vystup}: ${akce}?`, tlacitko: 'Odeslat' };
    }
};

/**
 * Informace v potvrzení příkazu u zařízení, které správce systému neschválil (E5). Jen informuje, odeslání neblokuje;
 * null = schválené nebo neznámý stav.
 */
export const upozorneniSchvaleni = (stavSchvaleni: string | null | undefined): string | null => {
    if (stavSchvaleni === 'NOVE') return 'Zařízení zatím neschválil správce systému. Příkaz se odešle, ale může být odmítnut.';
    if (stavSchvaleni === 'ZAMITNUTO') return 'Zařízení zamítl správce systému. Příkaz může být odmítnut.';
    return null;
};

/**
 * Potvrzení ručního příkazu (spec UX IoT 3.1). Fokus je na Zrušit, Esc nic neodešle. Dialog zůstává
 * otevřený, dokud příkaz neskončí (až 55 s), hlavní tlačítko mezitím ukazuje „Odesílám…“.
 */
const RucniOvladaniDialog: React.FC<{
    zarizeni: IotZarizeni; kanal: IotKanal; akce: string; spojeni: StavSpojeni; posledniKontaktMs?: number | null;
    odesila: boolean; onPotvrdit: (hodnota: number | null) => void; onClose: () => void;
}> = ({ zarizeni, kanal, akce, spojeni, posledniKontaktMs, odesila, onPotvrdit, onClose }) => {
    const par = parametrRucni(akce, kanal.typ);
    const [hodnota, setHodnota] = useState<number | null>(par?.vychozi ?? null);
    const texty = textyRucniAkce(akce, kanal.typ, kanal.nazev, hodnota);
    const platny = !par || parametrPlatny(par, hodnota);
    const kam = `${zarizeni.nazev}${zarizeni.umisteni ? ` (${zarizeni.umisteni})` : ''}`;
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{texty.nadpis}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2">Příkaz se hned pošle do zařízení {kam}.</Typography>
                    {spojeni.druh === 'nedostupne' && (
                        <Alert severity="warning">
                            {posledniKontaktMs != null
                                ? `Zařízení je teď nedostupné (naposledy ${formatNaposledy(posledniKontaktMs)}). Příkaz se nejspíš nedoručí.`
                                : 'Zařízení je teď nedostupné. Příkaz se nejspíš nedoručí.'}
                        </Alert>
                    )}
                    {upozorneniSchvaleni(zarizeni.stavSchvaleni) && (
                        <Alert severity="info">{upozorneniSchvaleni(zarizeni.stavSchvaleni)}</Alert>
                    )}
                    {par && (
                        <TextField type="number" label={par.label} value={hodnota ?? ''} error={!platny} disabled={odesila}
                            helperText={`${par.min} až ${par.max}.${texty.napoveda ? ` ${texty.napoveda}` : ''}`}
                            onChange={(e) => setHodnota(e.target.value === '' ? null : Number(e.target.value))} />
                    )}
                    {texty.veta && <Typography variant="body2" color="text.secondary">{texty.veta}</Typography>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={onClose}>{odesila ? 'Zavřít' : 'Zrušit'}</Button>
                <Button variant="contained" disabled={odesila || !platny} onClick={() => onPotvrdit(hodnota)}>
                    {odesila ? 'Odesílám…' : texty.tlacitko}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RucniOvladaniDialog;
